import type { TakeWinAnimation } from '../../effects/TakeWinAnimation';
import type { Logic } from '../Logic';
import type { SlotLogicContext } from './SlotLogicContext';

export interface CollectInput {
  /** Take-win animation widget; logic drives it via the per-coin onLand callback. */
  takeWin: TakeWinAnimation;
  /** Stage-local coordinates for the coin trail. Pass a single point for
   *  the legacy "all coins from one source" behaviour, or an array of
   *  points to spawn coins from each contributing winning cell. */
  source:
    | { x: number; y: number }
    | ReadonlyArray<{ x: number; y: number }>;
  target: { x: number; y: number };
  /** Optional per-coin side-effect — e.g. cosmetic balance-label bump. The
   * authoritative balance credit happens via model.credit. */
  onCoinArriveSideEffect?: () => void;
  /** Tuning passed straight through to {@link TakeWinAnimation#takeWin}. */
  totalDurationMs?: number;
  flyDurationMs?: number;
  maxCoins?: number;
}

/**
 * Drains the model's pendingWinnings into the player's balance via the
 * take-win flying-coin animation. Each coin's {@code onLand} credits the
 * model — the balance-changed listener in the scene handles the label
 * update, so the animation timing IS the counter timing.
 *
 * <p>No-op (resolves immediately) when pendingWinnings is 0 — used after
 * a losing gamble so the calling state machine can run a uniform
 * "collect → exit gamble sequence" tail.
 *
 * <p>Lifted-in-spirit from cs-core's {@code TakeWinLogic.ts}.
 */
export class CollectLogic implements Logic<CollectInput, void> {
  constructor(private readonly ctx: SlotLogicContext) {}

  async execute(input: CollectInput): Promise<void> {
    const { model } = this.ctx;
    const total = model.pendingWinnings;
    if (total <= 0) {
      // Drop pending immediately so the caller can sequence the next state.
      model.setPendingWinnings(0);
      return;
    }

    model.events.emit({ kind: 'collect-started', amount: total });
    // Producer audit Phase 4 — slowed the take-win cadence a notch so
    // the per-coin balance ticker is visibly perceptible. Prior
    // defaults (24 coins over 1400ms, 600ms flight) gave a ~28 Hz
    // arrival rate; the digit display flipped too fast for the eye to
    // read individual increments. New defaults: 18 coins over 2000ms
    // with 700ms flight = ~12 Hz arrival cadence — still snappy but
    // each arrival registers as a visible balance bump.
    await input.takeWin.takeWin(
      total,
      input.source,
      input.target,
      (value) => {
        model.credit(value, 'collect');
        input.onCoinArriveSideEffect?.();
      },
      {
        totalDurationMs: input.totalDurationMs ?? 2000,
        flyDurationMs: input.flyDurationMs ?? 700,
        maxCoins: input.maxCoins ?? 18,
      },
    );

    model.setPendingWinnings(0);
    model.events.emit({ kind: 'collect-completed', amount: total });
  }
}
